/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Popup from '@atlaskit/popup';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { LayoutOptions } from '@atlassian/company-hub-common/layout-options';
import type { PreviewSizeProps } from '@atlassian/company-hub-common/preview-size';

import { carouselConfigAnalyticSource } from '../../../../constants';
import { type UpdateFrameFormFieldType } from '../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import { CommonLayout, type Frame as FrameParameters } from '../../../../types';
import { CarouselLayoutRenderer } from '../../../carousel-content/layout-renderer';

import { messages } from './messages';

const DROPDOWN_WIDTH = 241;
const DROPDOWN_MAX_HEIGHT = 500;
const BUTTON_CONTAINER_WIDTH = 152;

export const layoutOptions: {
	label: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	value: CommonLayout;
}[] = [
	{
		label: messages.frameAboveImageLabel,
		value: CommonLayout.FRAME_ABOVE_IMAGE,
	},
	{
		label: messages.splitDesignLabel,
		value: CommonLayout.SPLIT_IMAGE_RIGHT,
	},
	{
		label: messages.splitDesignLabel,
		value: CommonLayout.SPLIT_IMAGE_LEFT,
	},
	{
		label: messages.solidColorBackgroundLabel,
		value: CommonLayout.NO_IMAGE_BACKGROUND,
	},
];

const styles = cssMap({
	dropdownMenuStyles: {
		width: `${DROPDOWN_WIDTH}px`,
		maxHeight: `${DROPDOWN_MAX_HEIGHT}px`,
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		overflowY: 'auto',
	},
	buttonContainer: {
		width: `${BUTTON_CONTAINER_WIDTH}px`,
		marginBottom: token('space.200'),
	},
});

export const CarouselLayoutOptions = ({
	updateFrameFormField,
	frameParameters,
	contentId,
}: {
	contentId: string;
	frameParameters: FrameParameters;
	updateFrameFormField: UpdateFrameFormFieldType;
}): JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);

	const handleUpdateLayout = useCallback(
		(newLayout: CommonLayout) =>
			updateFrameFormField(frameParameters.cardId, { layout: newLayout }),
		[frameParameters.cardId, updateFrameFormField],
	);

	const PreviewContentComponent = useCallback<PreviewSizeProps<CommonLayout>['PreviewComponent']>(
		({ height, layout }) => {
			return (
				<React.Fragment>
					<CarouselLayoutRenderer
						testId="carousel-spotlight-preview"
						backgroundColor={frameParameters.backgroundColor}
						height={height}
						layout={layout}
						imageData={frameParameters.imageSrc}
						title={frameParameters?.title}
						description={frameParameters?.description}
						textColor={frameParameters?.textColor}
						link={frameParameters.link}
						contentId={contentId}
						source="carousel-spotlight-preview"
					/>
				</React.Fragment>
			);
		},
		[frameParameters, contentId],
	);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			// editor toolbar has high z index
			zIndex={600}
			content={() => (
				<Flex direction="column" gap="space.150" xcss={cx(styles.dropdownMenuStyles)}>
					<LayoutOptions<CommonLayout>
						updateLayout={handleUpdateLayout}
						source={carouselConfigAnalyticSource}
						layoutOptions={layoutOptions}
						variant="compact"
						currentLayoutSelection={frameParameters.layout}
						PreviewComponent={PreviewContentComponent}
					/>
				</Flex>
			)}
			trigger={(triggerProps) => (
				<div css={styles.buttonContainer}>
					<Button
						{...triggerProps}
						isSelected={isOpen}
						onClick={() => setIsOpen(!isOpen)}
						iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					>
						<FormattedMessage {...messages.buttonTitle} />
					</Button>
				</div>
			)}
		/>
	);
};
